// This service handles the look up codes.

import { Injectable } from '@angular/core';
import { Observable, of } from 'rxjs';
import { filter, cloneDeep } from 'lodash';
import { rdsapLookupCodes } from '../../config/rdsap-lookup-codes.config';

@Injectable({
  providedIn: 'root'
})
export class RdSAPLookUpCodesService {
  private lookUpCodesData: any = {
    lookUpCodesByDataItem: {},
    lookUpCodesByDataItemThenValue: {},
    keyedLookUpCodes: {},
    wallsInsulationTypes: [],
    roofInsulationTypes: [],
    roofInsulationThicknesses: [],
    wallsInsulationTypesNoCavity: [],
    roofRoomsInsulationThicknesses: [],
    roofRoomsInsulationThicknessesWithoutNotApplicable: [],
    communityHeatingFuelCodes: [],
    pcdbSelectorHeatingTypes: [],
    swhConfigurations: [],
    keyedSWHConfigurations: {},
    pvConfigurationsOptions: [],
    keyedPVConfigurationsOptions: {}
  };

  private numberOfTurbinesOptions: any[] = [
    { dataItem: 'WindTurbines', lookUpItem: 'None', customLookName: 'None', lookupValue: 0 },
    { dataItem: 'WindTurbines', lookUpItem: '1', customLookName: '1', lookupValue: 1 },
    { dataItem: 'WindTurbines', lookUpItem: '2', customLookName: '2', lookupValue: 2 },
    { dataItem: 'WindTurbines', lookUpItem: '3', customLookName: '3', lookupValue: 3 },
    { dataItem: 'WindTurbines', lookUpItem: '4', customLookName: '4', lookupValue: 4 },
    { dataItem: 'WindTurbines', lookUpItem: '5', customLookName: '5', lookupValue: 5 }
  ];


  getRdSAPLookUpCodesData(): Observable<any> {
    const lookupCodes = rdsapLookupCodes;
    const windTurbines = lookupCodes.find((lookupCode) => lookupCode.dataItem === 'WindTurbines');
    if (!windTurbines) {
      lookupCodes.push(...this.numberOfTurbinesOptions);
    }

    lookupCodes.forEach((lookupCode) => {
      lookupCode.name = lookupCode.lookUpItem;
    });

    this.groupRdSAPLookUpCodes(lookupCodes);
    this.lookUpCodesData.lookupCodes = lookupCodes;

    return of(cloneDeep(this.lookUpCodesData));
  }

  private groupRdSAPLookUpCodes(lookUpCodesArray: any[]): void {
    const lookUpCodesByDataItem = {};
    const lookUpCodesByDataItemThenValue = {};
    const keyedLookUpCodes = {};
    const wallsInsulationTypes = [];
    const roofInsulationTypes = [];
    const roofInsulationThicknesses = [];
    const wallsInsulationTypesNoCavity = [];
    const roofRoomsInsulationThicknesses = [];
    const roofRoomsInsulationThicknessesWithoutNotApplicable = [];
    const communityHeatingFuelCodes = [];

    lookUpCodesArray.forEach((lookUpCode) => {
      const dataItem = lookUpCode.dataItem.trim();
      const lookUpItem = lookUpCode.lookUpItem.trim();
      const lookUpValue = lookUpCode.lookupValue;

      if (!keyedLookUpCodes[dataItem]) {
        keyedLookUpCodes[dataItem] = {};
      }

      keyedLookUpCodes[dataItem][lookUpItem] = lookUpCode.lookupValue;

      if (!lookUpCodesByDataItem[dataItem]) {
        lookUpCodesByDataItem[dataItem] = [];
      }

      lookUpCodesByDataItem[dataItem].push(lookUpCode);

      if (!lookUpCodesByDataItemThenValue[dataItem]) {
        lookUpCodesByDataItemThenValue[dataItem] = {};
      }

      lookUpCodesByDataItemThenValue[dataItem][lookUpValue] = lookUpItem;

      if (dataItem === 'WallInsulation') {
        wallsInsulationTypes.push(lookUpCode);
      }

      if (dataItem === 'WallInsulation' &&
        lookUpItem !== 'Filled cavity and internal' &&
        lookUpItem !== 'Filled cavity and external' &&
        lookUpItem !== 'Filled cavity') {

        wallsInsulationTypesNoCavity.push(lookUpCode);
      }

      if (dataItem === 'RoofInsulationLocation') {
        roofInsulationTypes.push(lookUpCode);
      }

      if (dataItem === 'RoofInsulationThickness') {
        roofInsulationThicknesses.push(lookUpCode);
     }

      if (dataItem === 'RoofRoomsInsulationThickness(AtCeiling)') {
        roofRoomsInsulationThicknesses.push(lookUpCode);
      }

      if (dataItem === 'RoofRoomsInsulationThickness(AtCeiling)' && lookUpItem !== 'Not applicable') {
        roofRoomsInsulationThicknessesWithoutNotApplicable.push(lookUpCode);
      }

      if (dataItem === 'HeatingFuel' && lookUpItem.includes('(community heating)')) {
        communityHeatingFuelCodes.push(lookUpCode);
     }

      const acceptedHeatingTypesForPcdb = ['Boiler',
        'Micro-cogeneration',
        'Heat pump (wet system)',
        'Heat pump (warm air)',
        'Community heating',
        'Electric storage',
        'Warm air'];

      const pcdbSelectorHeatingTypes = filter(lookUpCodesByDataItem['HeatingType'], function (heatingType) {
        return acceptedHeatingTypesForPcdb.indexOf(heatingType.lookUpItem) > -1;
      });

      this.lookUpCodesData = {
        lookUpCodesByDataItem: lookUpCodesByDataItem,
        keyedLookUpCodes: keyedLookUpCodes,
        lookUpCodesByDataItemThenValue: lookUpCodesByDataItemThenValue,
        wallsInsulationTypes: wallsInsulationTypes,
        roofInsulationTypes: roofInsulationTypes,
        roofInsulationThicknesses: roofInsulationThicknesses,
        wallsInsulationTypesNoCavity: wallsInsulationTypesNoCavity,
        roofRoomsInsulationThicknesses: roofRoomsInsulationThicknesses,
        roofRoomsInsulationThicknessesWithoutNotApplicable: roofRoomsInsulationThicknessesWithoutNotApplicable,
        communityHeatingFuelCodes: communityHeatingFuelCodes,
        pcdbSelectorHeatingTypes: pcdbSelectorHeatingTypes,
        swhConfigurations: [
            { name: 'No solar water heating', value: "None" },
            { name: 'Solar water heating (details unknown)', value: "DetailsUnknown" },
            { name: 'Solar water heating (details known)', value: "DetailsKnown" }
        ],
        keyedSWHConfigurations: {
            'No solar water heating': "None",
            'Solar water heating (details unknown)':  "DetailsUnknown",
            'Solar water heating (details known)': "DetailsKnown"
        },
        pvConfigurationsOptions: [
            { name: 'No PV system', value: "None" },
            { name: 'PV system (roof coverage only)', value: "roofCoverageOnly" },
            { name: '1 PV Array (kWp known)', value: "OnePVArray" },
            { name: '2 PV Arrays (kWp known)', value: "TwoPVArrays" },
            { name: '3 PV Arrays (kWp known)', value: "ThreePVArrays" }
        ],
        keyedPVConfigurationsOptions: {
            'No PV system': 0,
            'PV system (roof coverage only)': "roofCoverageOnly",
            '1 PV Array (kWp known)': "OnePVArray",
            '2 PV Arrays (kWp known)': "TwoPVArrays",
            '3 PV Arrays (kWp known)': "ThreePVArrays"
        }
      };
    });
  }
}
